/**
 * AtlasVolumetricLightModule — the SHADOWED light volume around the player
 * (shader additions wave, 2026-09-15): sun shafts between buildings and
 * through the canopy, the street lamps' cones at night, headlights in dust.
 *
 * One box (`VOLUME_SIZE_M`) follows the player in 8 m steps; its material
 * (engine `createShadowedLightVolume`) ray-marches every pixel the box covers
 * and evaluates the scene's lights WITH their shadow maps at each step — the
 * sun through the CSM, the lamp pool as point/spot lights — under an HG phase.
 * Nothing else in the frame changes: the volume is a transparent mesh drawn
 * after the opaques, attenuating what is behind it by its own transmittance.
 *
 * What drives it: the atmosphere's fog density (the same FogExp2 the sky
 * writes) and the weather set the extinction (`volumeDensityFor`); the night
 * factor thins the ambient floor so fog never glows in the dark; the weather's
 * wind drifts the noise; the tier picks the march steps. Off in space and
 * while the flag is off — the mesh is removed, so no pipeline is kept warm.
 *
 * THE FRAME. The mesh is a scene child, so its `position` is ABSOLUTE; the
 * box the shader tests is RENDER space (positionWorld / cameraPosition carry
 * the FP64 rebase) — `setBoxWorld` gets `mesh.position + scene.position`.
 *
 * v2 (2026-09-25, realism §8 R7 — crepuscular rays): the sun inside the box is
 * CLOUD-SHADOWED (the cloud field's own coverage map at the point where the path
 * toward the sun crosses the cloud base — the ground's cloud-shadow rule, so the
 * shafts fall exactly between the ground's cloud shadows), the medium shadows
 * ITSELF along the light path (two taps, 3 m and 10 m), and the ambient is the
 * drawn sky's mean radiance in the exposure curve's units (+ the night floor)
 * instead of a fixed blue.
 *
 * v3 LOCAL VOLUMES (shader gaps program round 3, agent F, 2026-09-30; engine ShadowedLightVolume v3, lib/volume_medium):
 *   · THE COMPOSITE — the in-scatter at full strength (v2's came out × (1 − T): three premultiplied an already
 *     premultiplied output), fog off, the MRT overlay mark (v2's box replaced the scene pass's normal / velocity /
 *     metal-rough under every pixel it covered).
 *   · ONE MEDIUM AROUND THE FIRES — the nearest ray-marched fires (≤ VOLUME_SPLIT_SLOTS within VOLUME_SPLIT_RANGE_M)
 *     split the march: the haze behind a fire is drawn before it, the haze in front of it after (renderOrder 9.5), so
 *     a campfire in a mist is dimmed by the mist in front of it — never composited over the whole volume.
 *   · HALF RESOLUTION (`volumeResolution`): a quarter of the pixels, rebuilt by a depth-aware composite (edge pixels
 *     marched in full).
 *   · THE JITTER (`volumeNoise`): 'golden' by default; 'stbn' loads the spatiotemporal blue-noise mask lazily
 *     (patented method — opt-in pending the owner); 'white' = v2's hash.
 *   · WINDOW LIGHTS in the medium: the area-light pool's rectangles are scattered (4 patches each) when `areaLights`
 *     is on — an approximation (unshadowed, like the pool).
 * Kill switches: `localStorage 'atlas:volumes'` = 'legacy' (v2 exactly) | 'full' | 'half' (overrides the preset);
 * `'atlas:volume-noise'` = 'white' | 'golden' | 'ign' | 'stbn'; `'atlas:volume-split' = 'off'`;
 * `'atlas:volume-rect' = 'off'`.
 *
 * Levers: `__lightVolume()` (v2's), `__volumes()` (v3: mode, noise, split boxes, half stats, `debug(1)` paints the
 * composite's full-res fallback pixels red).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { createShadowedLightVolume, type ShadowedLightVolumeHandle } from 'enginex/render/fog/ShadowedLightVolume.js';
import { loadSTBN, isSTBNReady, jitterModeCode, type JitterMode } from 'enginex/render/bluenoise/SpatiotemporalBlueNoise.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor, uSunDir } from './SkyModule.js';
import { lightFrame, exposureState, E0_LUX, NIGHT_FLOOR_LUX } from '../lib/photometry.js';
import { cloudShadowTexture } from '../lib/cloud_shadows.js';
import { CLOUD_FIELD_ROTATION, CLOUD_FIELD_SCALE_M, stage4Stats } from '../lib/stage4_fx.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { VOLUME_SIZE_M, volumeStepsFor, volumeDensityFor, volumeAmbientScale, stage5Stats } from '../lib/stage5_fx.js';
import { afterSubmitWindow } from '../lib/gpu_dispose.js';
import { fireSplitBoxes, volumeRectSamplesFor, volumeBuildKey, VOLUME_SPLIT_SLOTS, VOLUME_FRONT_ORDER, type SplitBox } from '../lib/volume_medium.js';
import { fireSystem } from '../lib/vfx/fire_system.js';
import { uFireJitterStbn } from '../lib/vfx/fire_volume.js';

const FOLLOW_STEP_M = 8;
const AMBIENT_DAY = new THREE.Vector3(0.03, 0.035, 0.045);
/** The sky in-scatter's calibration: 0.15 of the sky's mean radiance keeps the old day level (the box sits inside the height fog, which carries most of the sky haze). */
const VOLUME_SKY_GAIN = 0.15;
/** The low cloud étage's base (absolute m — WeatherModule's formation `low`). */
const CLOUD_BASE_ABS_M = 900;

function ls(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

/** What the preset and the kill switches ask for. */
interface VolumeWant { legacy: boolean; resolution: 'full' | 'half'; noise: JitterMode; split: number; rect: 0 | 4 }
function volumeWant(): VolumeWant {
  const p = activePreset();
  const k = ls('atlas:volumes');
  const legacy = k === 'legacy';
  const resolution: 'full' | 'half' = k === 'full' ? 'full' : k === 'half' ? 'half' : (p.volumeResolution === 'full' ? 'full' : 'half');
  const nk = ls('atlas:volume-noise');
  const noise: JitterMode = nk === 'white' || nk === 'golden' || nk === 'ign' || nk === 'stbn' ? nk : (p.volumeNoise ?? 'golden');
  const split = ls('atlas:volume-split') === 'off' ? 0 : VOLUME_SPLIT_SLOTS;
  const rect = volumeRectSamplesFor(!!p.areaLights, ls('atlas:volume-rect') === 'off');
  return { legacy, resolution, noise, split, rect };
}

export class AtlasVolumetricLightModule implements IModule {
  readonly name = 'VolumetricLight';
  private app!: App;
  private handle: ShadowedLightVolumeHandle | null = null;
  private anchorX = Number.NaN;
  private anchorZ = Number.NaN;
  private frame = 0;
  private lastTier = '';
  private buildKey = '';
  private want: VolumeWant = volumeWant();
  private splitBoxes: SplitBox[] = [];
  private noiseEffective: JitterMode = 'white';
  private readonly center = new THREE.Vector3();
  private readonly half = new THREE.Vector3(VOLUME_SIZE_M[0] / 2, VOLUME_SIZE_M[1] / 2, VOLUME_SIZE_M[2] / 2);
  private readonly camW = new THREE.Vector3();
  private lastMs = 0;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasVolumetricLight', Phase.RenderSub, 10, () => this.tick()));
    (globalThis as unknown as { __lightVolume?: () => unknown }).__lightVolume = () => ({
      built: !!this.handle,
      flag: !!activePreset().volumetricLights,
      density: this.handle?.uniforms.density.value ?? 0,
      steps: this.handle?.material.steps ?? 0,
      ambient: this.handle ? [this.handle.uniforms.ambient.value.x, this.handle.uniforms.ambient.value.y, this.handle.uniforms.ambient.value.z] : null,
      box: this.handle ? { min: this.handle.uniforms.boxMin.value.toArray(), max: this.handle.uniforms.boxMax.value.toArray() } : null,
      anchor: [this.anchorX, this.anchorZ],
      inScene: !!this.handle && this.handle.mesh.parent === this.app.world.scene,
      mode: this.handle?.stats.mode ?? null,
      half: this.handle ? { size: this.handle.stats.half, marches: this.handle.stats.marches, skipped: this.handle.stats.skipped } : null,
    });
    (globalThis as unknown as { __volumes?: () => unknown }).__volumes = () => ({
      built: !!this.handle,
      flag: !!activePreset().volumetricLights,
      want: { ...this.want },
      buildKey: this.buildKey,
      mode: this.handle?.stats.mode ?? null,
      noise: { requested: this.want.noise, effective: this.noiseEffective, stbnReady: isSTBNReady() },
      split: { slots: this.want.split, active: this.handle?.stats.splitActive ?? 0, boxes: this.splitBoxes.map((b) => ({ c: [+b.center.x.toFixed(2), +b.center.y.toFixed(2), +b.center.z.toFixed(2)], h: [+b.half.x.toFixed(2), +b.half.y.toFixed(2), +b.half.z.toFixed(2)] })) },
      half: this.handle ? { size: this.handle.stats.half, marches: this.handle.stats.marches, skipped: this.handle.stats.skipped } : null,
      rect: this.want.rect,
      /** Debug view: 1 paints the half composite's full-res fallback pixels red (0 = off). */
      debug: (v: number) => { if (this.handle) (this.handle.uniforms.debugView as { value: number }).value = v | 0; return v; },
      /** Re-read the preset + kill switches now (a probe that changed localStorage). */
      refresh: () => { this.want = volumeWant(); return { ...this.want }; },
    });
  }

  private ensure(on: boolean): void {
    const want = this.want;
    const tier = activeTier();
    const steps = volumeStepsFor(tier);
    const key = volumeBuildKey(want.resolution, want.legacy, want.rect, steps) + (want.split ? '/split' : '');
    if (on && this.handle && key !== this.buildKey) { this.teardown(); stage5Stats.volumeBuilt = false; }
    if (on && !this.handle) {
      this.handle = createShadowedLightVolume({
        size: VOLUME_SIZE_M, steps, density: 0.005, g: 0.55,
        cloudShadow: { texture: cloudShadowTexture(this.app), worldScale: CLOUD_FIELD_SCALE_M, rotation: CLOUD_FIELD_ROTATION, threshold: 0.4, softness: 0.3 },
        lightTaps: [3, 10],
        // v3 (agent F): legacy keeps v2 bit for bit; otherwise the correct composite, the split, the resolution, the rect patches.
        ...(want.legacy ? {} : {
          composite: 'correct' as const, resolution: want.resolution, jitter: 'golden' as const,
          splitBoxes: want.split, frontRenderOrder: VOLUME_FRONT_ORDER, rectSamples: want.rect,
        }),
      });
      if (!want.legacy) this.handle.attach(this.app.renderer.native, this.app.world.scene, this.app.renderer.cameras.main as THREE.Camera | undefined ?? null);
      this.buildKey = key;
      this.lastTier = tier;
      this.anchorX = Number.NaN; this.anchorZ = Number.NaN;
      this.app.world.scene.add(this.handle.mesh);
      stage5Stats.volumeBuilt = true;
    } else if (!on && this.handle) {
      this.teardown();
      this.buildKey = '';
      stage5Stats.volumeBuilt = false;
    }
  }

  /**
   * Detach NOW, free after the submit window (the GPU-teardown law): the handle owns more than its mesh tree (v3's
   * half-res targets, the depth copy, the private march scene), so its own dispose() runs — never the same frame.
   */
  private teardown(): void {
    const h = this.handle;
    if (!h) return;
    this.handle = null;
    h.mesh.removeFromParent();
    afterSubmitWindow(() => { try { h.dispose(); } catch { /* already freed */ } }, 8, 1200);
  }

  /** The jitter uniform: STBN only once its mask is loaded (golden meanwhile). */
  private applyNoise(h: ShadowedLightVolumeHandle): void {
    let n = this.want.noise;
    if (n === 'stbn' && !isSTBNReady()) { void loadSTBN(); n = 'golden'; }
    if (this.want.legacy) n = 'white';
    this.noiseEffective = n;
    (h.uniforms.jitterMode as { value: number }).value = jitterModeCode(n);
    // The fires' box march follows onto STBN (its own decorrelated sequence); anything else keeps the fire's IGN.
    (uFireJitterStbn as unknown as { value: number }).value = n === 'stbn' ? 1 : 0;
  }

  /** The nearest ray-marched fires inside the box split the march (render space). */
  private applySplit(h: ShadowedLightVolumeHandle): void {
    if (!this.want.split || this.want.legacy) { this.splitBoxes = []; return; }
    const fires = (fireSystem()?.describe().fires ?? []) as Array<{ rung?: string; at: number[]; size: number }>;
    const cam = this.app.renderer.cameras.main as THREE.Camera | undefined;
    cam?.getWorldPosition(this.camW);
    const sp = this.app.world.scene.position;
    this.splitBoxes = fireSplitBoxes(fires, this.camW, sp, h.uniforms.boxMin.value, h.uniforms.boxMax.value, this.want.split);
    h.setSplitBoxes(this.splitBoxes);
  }

  private tick(): void {
    this.frame++;
    const p = activePreset();
    if (this.frame % 30 === 1) this.want = volumeWant();
    const want = !!p.volumetricLights && !atlas.spaceFlight && atlas.currentWorldId != null && !atlas.loadingGate;
    this.ensure(want);
    const h = this.handle;
    if (!h) return;
    // Follow the player in steps (absolute), the box floor just under the feet.
    const px = atlas.localX, pz = atlas.localZ;
    if (Number.isNaN(this.anchorX) || Math.hypot(px - this.anchorX, pz - this.anchorZ) > FOLLOW_STEP_M) {
      this.anchorX = Math.round(px / FOLLOW_STEP_M) * FOLLOW_STEP_M;
      this.anchorZ = Math.round(pz / FOLLOW_STEP_M) * FOLLOW_STEP_M;
    }
    h.mesh.position.set(this.anchorX, atlas.localY + this.half.y - 2.5, this.anchorZ);
    // The shader tests RENDER space: fold the rebase in.
    const sp = this.app.world.scene.position;
    this.center.set(h.mesh.position.x + sp.x, h.mesh.position.y + sp.y, h.mesh.position.z + sp.z);
    h.setBoxWorld(this.center, this.half);
    // Steps follow the tier live (an int uniform + the material's own count).
    const tier = activeTier();
    if (tier !== this.lastTier) {
      this.lastTier = tier;
      const steps = volumeStepsFor(tier);
      h.material.steps = steps;
      (h.uniforms.steps as { value: number }).value = steps;
      h.material.needsUpdate = true;
    }
    // Density from the sky's own fog + the weather, at ~10 Hz.
    if (this.frame % 6 === 0) {
      this.applyNoise(h);
      this.applySplit(h);
      const fog = this.app.world.scene.fog as THREE.FogExp2 | null;
      const weather = this.app.getModule('Weather') as { getPrecip?: () => { kind: string; density: number }; getDust?: () => number; wind?: () => { x: number; z: number; strength: number } } | undefined;
      const kind = weather?.getPrecip?.().kind ?? 'clear';
      const dust = weather?.getDust?.() ?? 0;
      const density = volumeDensityFor(fog?.density ?? 0.002, kind, dust);
      h.uniforms.density.value = density;
      stage5Stats.volumeDensity = density;
      stage5Stats.volumeSteps = h.material.steps;
      const night = (uNightFactor as unknown as { value: number }).value ?? 0;
      const lm = lightFrame.model;
      if (lm) {
        // The sky's own in-scatter (scene units), calibrated to the old day level; the night floor below it.
        const e0s = E0_LUX * exposureState.luxToScene, sm = lightFrame.skyMean;
        const fl = ((lightFrame.nightLux ?? NIGHT_FLOOR_LUX) * exposureState.luxToScene) / Math.PI; // sky/post (2026-09-27): + the moon
        h.uniforms.ambient.value.set((sm.x * e0s + fl * 0.55) * VOLUME_SKY_GAIN, (sm.y * e0s + fl * 0.66) * VOLUME_SKY_GAIN, (sm.z * e0s + fl) * VOLUME_SKY_GAIN);
      } else {
        const a = volumeAmbientScale(night);
        h.uniforms.ambient.value.set(AMBIENT_DAY.x * a, AMBIENT_DAY.y * a, AMBIENT_DAY.z * a);
      }
      // v2: the sun through the cloud field (the ground's cloud-shadow drift, strength and the low étage's base).
      const hu = h.uniforms as typeof h.uniforms & { sunDir?: { value: THREE.Vector3 } };
      const sd = uSunDir.value as unknown as THREE.Vector3;
      hu.sunDir?.value.set(sd.x, sd.y, sd.z);
      const clouds = this.app.getModule('Clouds') as { renderer?: { windOffset?: { x: number; y: number } } } | undefined;
      const wind = clouds?.renderer?.windOffset ?? null;
      const spc = this.app.world.scene.position;
      h.uniforms.cloudOffset.value.set((wind?.x ?? 0) - spc.x, (wind?.y ?? 0) - spc.z);
      h.uniforms.cloudStrength.value = stage4Stats.cloudShadowStrength;
      h.uniforms.cloudBaseY.value = CLOUD_BASE_ABS_M + spc.y;
      // The noise drifts with the wind.
      const w = weather?.wind?.();
      const now = performance.now();
      const dt = this.lastMs ? Math.min(0.5, (now - this.lastMs) / 1000) : 0;
      this.lastMs = now;
      if (w) { const wv = h.uniforms.wind.value; wv.x += (w.x * w.strength * 0.6 * dt) / 9; wv.z += (w.z * w.strength * 0.6 * dt) / 9; }
    }
  }

  dispose(): void { this.ensure(false); }
}
